import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { 
  Phone, Network, Server, Shield, Monitor, Lightbulb, Sun,
  CheckCircle, ArrowRight, Database, Building, ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SectionHeader } from '../components/common/SectionHeader';
import { CTASection } from '../components/common/CTASection';
import { GeometricBackground } from '../components/decorations/GeometricBackground';
import { services, deliveryMethodology, getRelatedServices } from '../data/services';

const iconMap = {
  Phone,
  Network,
  Server,
  Shield,
  Monitor,
  Lightbulb,
  Sun,
  Database,
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);
  const relatedServices = getRelatedServices(service?.id);

  // Redirect to renewable energy page if that's the service
  if (slug === 'renewable-energy') {
    return <Navigate to="/services/renewable-energy" replace />;
  }

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const IconComponent = iconMap[service.icon] || Server;
  const colorClass = service.color === 'accent' ? 'accent' : 'primary';

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-hero-pattern overflow-hidden">
        <GeometricBackground />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Badge className={`mb-4 ${colorClass === 'accent' ? 'bg-accent/20 border-accent/30' : 'bg-primary-foreground/10 border-primary-foreground/20'} text-primary-foreground`}>
              Our Services
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {service.name}
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/80 mb-8">
              {service.valueStatement}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild variant="hero-accent" size="xl">
                <Link to="/contact">
                  Request Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="hero-outline" 
                size="xl" 
                className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
              >
                <Link to={`/products?type=${encodeURIComponent(service.name.split(' ')[0])}`}>
                  View Related Products
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Deliver Section */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className={`w-16 h-16 rounded-2xl ${colorClass === 'accent' ? 'bg-accent/10' : 'bg-primary/10'} flex items-center justify-center mb-6`}>
                <IconComponent className={`w-8 h-8 ${colorClass === 'accent' ? 'text-accent' : 'text-primary'}`} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                What We Deliver
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed text-lg">
                {service.shortDescription}
              </p>

              {/* Benefits */}
              {service.benefits && (
                <div className="mb-8">
                  <h4 className="font-semibold text-foreground mb-4">Key Benefits:</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {service.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${colorClass === 'accent' ? 'text-accent' : 'text-primary'}`} />
                        <span className="text-sm text-muted-foreground">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Settings (for AV service) */}
              {service.settings && (
                <div className="mb-8">
                  <h4 className="font-semibold text-foreground mb-4">Applicable Settings:</h4>
                  <div className="flex flex-wrap gap-2">
                    {service.settings.map((setting, i) => (
                      <Badge key={i} variant="secondary">{setting}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Offerings Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <IconComponent className={`w-5 h-5 ${colorClass === 'accent' ? 'text-accent' : 'text-primary'}`} />
                    Our Offerings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {service.offerings.map((offering, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${colorClass === 'accent' ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'}`}>
                          <span className="text-xs font-bold">{i + 1}</span>
                        </div>
                        <span className="text-muted-foreground">{offering}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Addons (for Cyber Security - Data Backup) */}
                  {service.addons && service.addons.map((addon, i) => (
                    <div key={i} className="mt-8 pt-6 border-t border-border">
                      <h5 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Database className="w-5 h-5 text-primary" />
                        {addon.name}
                      </h5>
                      <ul className="space-y-2 mb-4">
                        {addon.offerings.map((item, j) => (
                          <li key={j} className="flex items-start gap-3">
                            <CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span className="text-sm text-muted-foreground">{item}</span>
                          </li>
                        ))}
                      </ul>
                      {addon.note && (
                        <p className="text-xs text-muted-foreground italic bg-muted/50 p-3 rounded-lg">
                          {addon.note}
                        </p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-12 bg-card">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <img
              src={service.image}
              alt={`${service.name} - African professionals`}
              className="w-full h-[600px] object-cover rounded-2xl shadow-xl"
            />
          </motion.div>
        </div>
      </section>

      {/* How We Deliver Section */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionHeader
            subtitle="Our Approach"
            title="How We Deliver"
            description="A proven methodology that ensures successful project outcomes and lasting value."
            className="mb-16"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deliveryMethodology.map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className={`w-10 h-10 rounded-full ${colorClass === 'accent' ? 'bg-accent text-accent-foreground' : 'bg-primary text-primary-foreground'} flex items-center justify-center mb-4 font-bold`}>
                      {index + 1}
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{phase.step}</h3>
                    <p className="text-sm text-muted-foreground">{phase.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Typical Use Cases Section */}
      {service.useCases && (
        <section className="section-padding bg-card">
          <div className="container-custom">
            <SectionHeader
              subtitle="Applications"
              title="Typical Use Cases"
              description="Real-world scenarios where our solutions deliver measurable impact."
              className="mb-16"
            />

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.useCases.map((useCase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="h-full hover:shadow-lg hover:border-primary/30 transition-all">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg ${colorClass === 'accent' ? 'bg-accent/10' : 'bg-primary/10'} flex items-center justify-center shrink-0`}>
                        <Building className={`w-5 h-5 ${colorClass === 'accent' ? 'text-accent' : 'text-primary'}`} />
                      </div>
                      <p className="text-muted-foreground">{useCase}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Supported Technologies Section */}
      {/* {service.supportedTech && service.supportedTech.length > 0 && (
        <section className="section-padding bg-background">
          <div className="container-custom">
            <SectionHeader
              subtitle="Technology Stack"
              title="Supported Technologies"
              description={service.supportedTechNote || "We work with industry-leading technology brands to deliver reliable solutions."}
              className="mb-12"
            />

            <div className="flex flex-wrap justify-center gap-8">
              {service.supportedTech.map((tech, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center justify-center"
                >
                  {tech.logo ? (
                    <div className="h-12 md:h-16 flex items-center justify-center px-6 py-4 bg-card rounded-xl border border-border hover:shadow-lg transition-shadow">
                      <img
                        src={tech.logo}
                        alt={tech.name}
                        className="max-h-8 md:max-h-10 w-auto object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <span className="hidden text-sm font-medium text-muted-foreground">{tech.name}</span>
                    </div>
                  ) : (
                    <div className="px-6 py-4 bg-card rounded-xl border border-border hover:shadow-lg transition-shadow">
                      <span className="text-sm font-medium text-muted-foreground">{tech.name}</span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )} */}

      {/* Related Services Section */}
      <section className="section-padding bg-card">
        <div className="container-custom">
          <SectionHeader
            subtitle="Explore More"
            title="Related Services"
            description="Discover other solutions that complement this service."
            className="mb-12"
          />

          <div className="grid md:grid-cols-3 gap-6">
            {relatedServices.map((related, index) => {
              const RelatedIcon = iconMap[related.icon] || Server;
              return (
                <motion.div
                  key={related.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={related.route}>
                    <Card className="h-full hover:shadow-xl hover:border-primary/30 hover:-translate-y-1 transition-all cursor-pointer">
                      <CardContent className="p-6">
                        <div className={`w-12 h-12 rounded-xl ${related.color === 'accent' ? 'bg-accent/10' : 'bg-primary/10'} flex items-center justify-center mb-4`}>
                          <RelatedIcon className={`w-6 h-6 ${related.color === 'accent' ? 'text-accent' : 'text-primary'}`} />
                        </div>
                        <h3 className="font-semibold text-foreground mb-2">{related.name}</h3>
                        <p className="text-sm text-muted-foreground mb-4">{related.shortDescription}</p>
                        <span className={`inline-flex items-center text-sm font-medium ${related.color === 'accent' ? 'text-accent' : 'text-primary'}`}>
                          Learn More
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Talk to an Expert"
        description={`Ready to transform your ${service.name.toLowerCase().includes('security') ? 'security posture' : 'technology infrastructure'}? Our team is here to help.`}
        primaryButtonText="Request Consultation"
        primaryButtonLink="/contact"
        variant={colorClass === 'accent' ? 'accent' : 'default'}
      />
    </div>
  );
};

export default ServiceDetailPage;
